import api from '../../../Api';
import TitleType from '../../../types/Title';
import { DrcProducts } from '../../../types';
import { useQuery } from 'react-query';
import CacheKeysEnum from '../../../CacheKeysEnum';

export type UseWebReaderProductsType = {
	isLoading: boolean;
	getActive: () => TitleType[];
	getExpired: () => TitleType[];
	exists: (skuOrEan: string) => boolean;
	isActive: (skuOrEan: string) => boolean;
	isExpired: (skuOrEan: string) => boolean;
	getLastAccessedDate: (sku: string) => string | null;
	getPercentOfBookCompleted: (sku: string) => number;
};

const initialState: DrcProducts = {
	activeProducts: [],
	expiredProducts: [],
	lastReadPositionBySku: {},
};

export default function useWebReaderProducts(): UseWebReaderProductsType {
	const { data: webReaderDrcProducts, isLoading } = useQuery<DrcProducts>(
		CacheKeysEnum.WebReaderDrcProducts,
		fetchWebReaderProducts,
	);

	const getWebReaderDrcProducts = (): DrcProducts => {
		if (!webReaderDrcProducts) return initialState;
		return webReaderDrcProducts;
	};

	const webReaderProducts: DrcProducts = getWebReaderDrcProducts();

	const getAllProducts = () => {
		return [
			...webReaderProducts.activeProducts,
			...webReaderProducts.expiredProducts,
		];
	};

	const hasLastReadPosition = (sku: string): boolean => {
		return (
			webReaderProducts.lastReadPositionBySku &&
			webReaderProducts.lastReadPositionBySku[sku] &&
			typeof webReaderProducts.lastReadPositionBySku[sku] === 'object' &&
			webReaderProducts.lastReadPositionBySku[sku] !== null &&
			Object.keys(webReaderProducts.lastReadPositionBySku[sku]).length ===
				2
		);
	};

	return {
		isLoading,
		getActive: () => webReaderProducts.activeProducts,
		getExpired: () => webReaderProducts.expiredProducts,
		exists: (skuOrEan: string) => exists(skuOrEan, getAllProducts()),
		isActive: (skuOrEan: string) =>
			exists(skuOrEan, webReaderProducts.activeProducts),
		isExpired: (skuOrEan: string) =>
			exists(skuOrEan, webReaderProducts.expiredProducts),
		getLastAccessedDate: (sku: string): string | null => {
			if (hasLastReadPosition(sku)) {
				return webReaderProducts.lastReadPositionBySku[sku].created;
			}
			return null;
		},
		getPercentOfBookCompleted: (sku: string): number => {
			if (hasLastReadPosition(sku)) {
				return webReaderProducts.lastReadPositionBySku[sku]
					.percentOfBookCompleted;
			}
			return 0;
		},
	};
}

async function fetchWebReaderProducts(): Promise<DrcProducts> {
	try {
		const endpoint = '/api/me/galleys/reader/viewed';
		const drcProducts: DrcProducts = await api.get(endpoint);
		return drcProducts;
	} catch (err) {
		return initialState;
	}
}

function exists(skuOrEan: string, products: TitleType[]): boolean {
	const skus: string[] = getSkus(products);
	const eans: string[] = getEans(products);
	return skus.includes(skuOrEan) || eans.includes(skuOrEan);
}

const isValid = (sku: string) => typeof sku === 'string' && sku.length > 0;

function getSkus(products: TitleType[]): string[] {
	return select(products, 'sku');
}

function getEans(products: TitleType[]): string[] {
	return select(products, 'ean');
}

function select(products: TitleType[], field: string): string[] {
	return products.filter(p => isValid(p[field])).map(p => p[field]);
}
